/**
 * Repositorio de grabaciones.
 *
 * El blob local es CACHÉ. Hasta la Fase 8 la única copia durable posible es la
 * descarga manual al dispositivo (Ajuste 3), y por eso `descargadaManualmente`
 * existe desde ahora: permite avisar de una grabación crítica sin respaldo.
 */

import { liveQuery, type Observable } from 'dexie';

import {
  esCritica,
  type EtiquetaGrabacion,
  type Grabacion,
} from '../domain/modelo/grabacion.js';
import { nuevoId } from '../domain/modelo/ids.js';
import {
  metadatosBorrados,
  metadatosEditados,
  metadatosNuevos,
} from '../domain/modelo/sincronizacion.js';
import { db, escribir } from './db.js';

export interface DatosGrabacionNueva {
  sesionId?: string;
  stepKey?: string;
  etiqueta: EtiquetaGrabacion;
  nombre: string;
  segundos: number;
  mimeType: string;
  blobLocal?: Blob;
}

export const grabacionesRepo = {
  async listar(): Promise<Grabacion[]> {
    return (await db.grabaciones.toArray()).filter((g) => !g.deleted);
  },

  observarTodas(): Observable<Grabacion[]> {
    return liveQuery(async () => grabacionesRepo.listar());
  },

  /** Las grabaciones vivas de una sesión. */
  async porSesion(sesionId: string): Promise<Grabacion[]> {
    return (await grabacionesRepo.listar()).filter((g) => g.sesionId === sesionId);
  },

  observarPorSesion(sesionId: string): Observable<Grabacion[]> {
    return liveQuery(async () => grabacionesRepo.porSesion(sesionId));
  },

  /** La grabación de un paso concreto, si ya existe. */
  async dePaso(sesionId: string, stepKey: string): Promise<Grabacion | undefined> {
    return (await grabacionesRepo.porSesion(sesionId)).find((g) => g.stepKey === stepKey);
  },

  async obtener(id: string): Promise<Grabacion | undefined> {
    const grabacion = await db.grabaciones.get(id);
    return grabacion && !grabacion.deleted ? grabacion : undefined;
  },

  async crear(datos: DatosGrabacionNueva): Promise<Grabacion> {
    const grabacion: Grabacion = {
      id: nuevoId(),
      sesionId: datos.sesionId,
      stepKey: datos.stepKey,
      etiqueta: datos.etiqueta,
      nombre: datos.nombre,
      segundos: datos.segundos,
      mimeType: datos.mimeType,
      blobLocal: datos.blobLocal,
      subida: false,
      descargadaManualmente: false,
      creadoEn: new Date().toISOString(),
      ...metadatosNuevos(),
    };
    await escribir('guardar la grabación', () => db.grabaciones.add(grabacion));
    return grabacion;
  },

  async guardar(grabacion: Grabacion): Promise<Grabacion> {
    const actualizada = { ...grabacion, ...metadatosEditados(grabacion) };
    await escribir('guardar la grabación', () => db.grabaciones.put(actualizada));
    return actualizada;
  },

  async marcarDescargada(id: string): Promise<void> {
    const grabacion = await grabacionesRepo.obtener(id);
    if (!grabacion) return;
    await grabacionesRepo.guardar({ ...grabacion, descargadaManualmente: true });
  },

  /**
   * Grabaciones de baseline o checkpoint sin ninguna copia fuera de IndexedDB.
   *
   * Son las que sostienen la comparación longitudinal del programa: perderlas
   * no se puede deshacer. La interfaz las señala para que el usuario baje una
   * copia (Ajuste 3).
   */
  async criticasSinRespaldo(): Promise<Grabacion[]> {
    return (await grabacionesRepo.listar()).filter(
      (g) => esCritica(g) && !g.descargadaManualmente && !g.subida,
    );
  },

  async borrar(id: string): Promise<void> {
    const grabacion = await db.grabaciones.get(id);
    if (!grabacion) return;
    await escribir('borrar la grabación', () =>
      db.grabaciones.put({ ...grabacion, ...metadatosBorrados(grabacion) }),
    );
  },

  async pendientesDeSincronizar(): Promise<Grabacion[]> {
    return (await db.grabaciones.toArray()).filter((g) => g.dirty);
  },
};
